import {
  useQuery,
  useQueryClient,
  type QueryClient,
  type QueryFunctionContext,
} from "@tanstack/react-query";
import { apiService, sessionService, type Company, type User } from "../services/api";

const POLL_MS = 15 * 60 * 1000;
const STALE_MS = 14 * 60 * 1000;
const GC_MS = 15 * 60 * 1000;

export type AuthSyncPayload = {
  company: Company | null;
  user: User;
};

type AuthSyncKey = readonly ["auth-sync", string | undefined, string | undefined];

/**
 * One interval for both `/api/auth/me` and `/api/companies/me` (when user has a company).
 * Writes `['current-user', id]` and `['my-company', companyId]` so the rest of the app stays keyed the same.
 */
export function useAuthSyncQuery(user: User | null) {
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: ["auth-sync", user?._id, user?.companyId] as const,
    queryFn: (ctx) => runAuthSync(ctx, queryClient),
    enabled: !!user,
    refetchInterval: POLL_MS,
    staleTime: STALE_MS,
    gcTime: GC_MS,
    refetchIntervalInBackground: false,
  });
}

async function runAuthSync(
  { queryKey }: QueryFunctionContext,
  client: QueryClient,
): Promise<AuthSyncPayload> {
  const [, , companyId] = queryKey as AuthSyncKey;

  const companyPromise: Promise<Company | null> = companyId
    ? apiService.getMyCompany().catch((e) => {
        console.error("auth-sync: company fetch failed", e);
        return null;
      })
    : Promise.resolve(null);

  const [freshUser, company] = await Promise.all([
    apiService.getCurrentUser(),
    companyPromise,
  ]);
  sessionService.setSession(freshUser);
  client.setQueryData<User>(["current-user", freshUser._id], freshUser);
  if (companyId && company) {
    client.setQueryData<Company>(["my-company", companyId], company);
  }

  return { company, user: freshUser };
}
